import {
  Button,
  Col,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IStoreBannersService } from "@shared/interfaces/store-banners-service.interface";
import { StoreBannerStatus } from "@shared/types/store-banner/enums/store-banner-status.enum";
import { StoreBannerDto } from "@shared/types/store-banner/store-banner.dto";
import { ColumnsType } from "antd/es/table";
import { useMutation, useQuery } from "react-query";
import AlertModal from "../Popups/AlertModal/AlertModal";
import ChangeStoreBannerModal from "./ChangeStoreBannerModal/ChangeStoreBannerModal";
// Note: Keeping AlertModal reused from Popups or better to duplicate? Using existing for now.

const ALL = "All";
const STATUSES = [
  { label: "All", value: ALL },
  { label: "Active", value: StoreBannerStatus.ACTIVE },
  { label: "Inactive", value: StoreBannerStatus.INACTIVE },
];

const StoreBanners = () => {
  const [status, setStatus] = useState(ALL);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);

  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<StoreBannerDto | null>(
    null,
  );

  const storeBannersService = appInject<IStoreBannersService>(
    DI_TOKENS.storeBannersService,
  );

  const columns = useMemo(
    () =>
      [
        {
          key: "id",
          dataIndex: "id",
          title: "ID",
          width: 300,
        },
        {
          key: "title",
          dataIndex: "title",
          title: "Title",
          width: 200,
        },
        {
          key: "description",
          dataIndex: "description",
          title: "Description",
          width: 300,
        },
        {
          key: "status",
          dataIndex: "status",
          title: "Status",
          width: 100,
          render: (status: StoreBannerStatus) => (
            <Tag
              color={
                status === StoreBannerStatus.ACTIVE ? "#009E60" : "#f5222d"
              }
            >
              {status === StoreBannerStatus.ACTIVE ? "Active" : "Inactive"}
            </Tag>
          ),
        },
        {
          key: "createdAt",
          dataIndex: "createdAt",
          title: "Created At",
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD hh:mm:ss A") : "N/A",
          width: 200,
        },
        {
          key: "action",
          title: "Actions",
          render: (_: null, banner: StoreBannerDto) => (
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setSelectedBanner(banner);
                  setIsOpenModal(true);
                }}
              >
                Edit
              </Button>
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => {
                  setSelectedBanner(banner);
                  setIsAlertModalOpen(true);
                }}
              >
                Delete
              </Button>
            </Space>
          ),
          width: 180,
        },
      ] as ColumnsType<StoreBannerDto>,
    [],
  );

  const bannersQuery = useQuery(
    ["store-banners-list", page, currentPageSize, debouncedSearchText, status],
    () =>
      storeBannersService.list({
        page,
        pageSize: currentPageSize,
        search: debouncedSearchText,
        status: status === ALL ? undefined : status,
      }),
    {
      refetchOnMount: true,
    },
  );

  const deleteMutation = useMutation(
    (id: string) => storeBannersService.delete(id),
    {
      onSuccess: () => {
        setIsAlertModalOpen(false);
        setSelectedBanner(null);
        bannersQuery.refetch();
      },
    },
  );

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  const containerRef = useRef<any>();

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  return (
    <Layout style={{ height: "100%", padding: "20px" }}>
      {isOpenModal && (
        <ChangeStoreBannerModal
          campaigns={bannersQuery.data?.campaigns ?? []}
          open={isOpenModal}
          banner={selectedBanner}
          onClose={() => {
            setIsOpenModal(false);
            setSelectedBanner(null);
          }}
          onSuccess={() => {
            setIsOpenModal(false);
            setSelectedBanner(null);
            bannersQuery.refetch();
          }}
        />
      )}

      {isAlertModalOpen && (
        <AlertModal
          open={isAlertModalOpen}
          description={`Are you sure you want to delete "${selectedBanner?.title}"?`}
          onClose={() => setIsAlertModalOpen(false)}
          onProceed={() =>
            selectedBanner?.id && deleteMutation.mutate(selectedBanner.id)
          }
          isLoading={deleteMutation.isLoading}
          error={deleteMutation.error ? "Failed to delete" : undefined}
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={4}>
            Store Banners ({bannersQuery.data?.total || 0})
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search by Title"
              value={searchText}
              onChange={handleSearch}
              style={{ width: 200 }}
            />
            <Select
              options={STATUSES}
              value={status}
              onChange={setStatus}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              onClick={() => {
                setSelectedBanner(null);
                setIsOpenModal(true);
              }}
            >
              Add Banner
            </Button>
          </Space>
        </Col>
      </Row>

      <Layout.Content
        ref={containerRef}
        style={{ flex: 1, backgroundColor: "white" }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={bannersQuery.data?.banners || []}
          loading={bannersQuery.isFetching}
          pagination={{
            current: page,
            total: bannersQuery.data?.total || 0,
            pageSize: currentPageSize,
            onChange: (page, pageSize) =>
              onTableChange({ current: page, pageSize }),
          }}
          scroll={{ y: containerHeight }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default StoreBanners;
